import { Injectable, inject, signal } from '@angular/core';
import {
  collection,
  doc,
  serverTimestamp,
  setDoc,
  writeBatch,
} from 'firebase/firestore';

import { FirebaseService, SYNC_SCHEMA_VERSION } from './firebase.service.js';
import { IdbService } from './idb.service.js';
import { SavedChannel, StreamProject } from '../models/library.model.js';
import { GlobalSettings } from './sync.service.js';

const STORAGE_KEYS = {
  CHANNELS: 'tw_analyzer_saved_channels',
  PROJECTS: 'tw_analyzer_stream_projects',
  SESSION: 'twitch-analyzer-session',
  SEARCH_HISTORY: 'twitch-analyzer-search-history',
};

const FIRESTORE_BATCH_LIMIT = 500;
// Each project consumes 2 batch ops (metadata + state); split conservatively.
const PROJECTS_PER_BATCH = 200;

export interface MigrationProgress {
  phase: 'channels' | 'projects' | 'state' | 'settings' | 'finalize' | 'done' | 'error';
  current: number;
  total: number;
  message: string;
}

/**
 * One-shot migration of locally-stored library/state into Firestore the first
 * time a Pro user logs in after the cloud-sync release. Idempotent: re-running
 * with already-migrated data simply overwrites in place. Caller is expected to
 * gate on `userProfile.migrationCompleted !== true`.
 */
@Injectable({ providedIn: 'root' })
export class SyncMigrationService {
  private firebaseService = inject(FirebaseService);
  private idbService = inject(IdbService);

  progress = signal<MigrationProgress | null>(null);
  isRunning = signal<boolean>(false);

  countLocalProjects(): number {
    try {
      const raw = localStorage.getItem(STORAGE_KEYS.PROJECTS);
      if (!raw) return 0;
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.length : 0;
    } catch {
      return 0;
    }
  }

  countLocalChannels(): number {
    try {
      const raw = localStorage.getItem(STORAGE_KEYS.CHANNELS);
      if (!raw) return 0;
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.length : 0;
    } catch {
      return 0;
    }
  }

  async runFirstTimeMigration(uid: string): Promise<void> {
    if (this.isRunning()) return;

    const fs = this.firebaseService.firestore;
    if (!fs) throw new Error('Firestore не инициализирован');

    this.isRunning.set(true);

    try {
      // ----- Channels -----
      const channels = this.readJson<SavedChannel[]>(STORAGE_KEYS.CHANNELS) ?? [];
      this.progress.set({
        phase: 'channels',
        current: 0,
        total: channels.length,
        message: `Загрузка каналов (${channels.length})...`,
      });
      await this.uploadChannels(uid, channels);
      this.progress.set({
        phase: 'channels',
        current: channels.length,
        total: channels.length,
        message: 'Каналы загружены',
      });

      // ----- Projects (metadata) + per-project state -----
      const projects = this.readJson<StreamProject[]>(STORAGE_KEYS.PROJECTS) ?? [];
      this.progress.set({
        phase: 'projects',
        current: 0,
        total: projects.length,
        message: `Загрузка проектов (${projects.length})...`,
      });
      await this.uploadProjectsMetadata(uid, projects);
      this.progress.set({
        phase: 'state',
        current: 0,
        total: projects.length,
        message: 'Загрузка состояний проектов...',
      });
      await this.uploadProjectStates(uid, projects);

      // Active session (if any, distinct from list of projects)
      await this.uploadActiveSession(uid, projects);

      // ----- Settings -----
      this.progress.set({ phase: 'settings', current: 0, total: 1, message: 'Настройки...' });
      await this.uploadSettings(uid);

      // ----- Finalize -----
      this.progress.set({ phase: 'finalize', current: 0, total: 1, message: 'Завершение...' });
      await setDoc(
        doc(fs, 'users', uid),
        {
          migrationCompleted: true,
          schemaVersion: SYNC_SCHEMA_VERSION,
          syncEnabled: true,
          lastSyncedAt: serverTimestamp(),
        },
        { merge: true },
      );

      // Refresh local profile cache so isSyncEligible reflects new state immediately.
      const current = this.firebaseService.userProfile();
      if (current) {
        this.firebaseService.userProfile.set({
          ...current,
          migrationCompleted: true,
          schemaVersion: SYNC_SCHEMA_VERSION,
          syncEnabled: true,
        });
      }

      this.progress.set({
        phase: 'done',
        current: projects.length,
        total: projects.length,
        message: 'Миграция завершена',
      });
    } catch (err) {
      console.error('SyncMigrationService.runFirstTimeMigration failed:', err);
      this.progress.set({
        phase: 'error',
        current: 0,
        total: 0,
        message: err instanceof Error ? err.message : 'Неизвестная ошибка миграции',
      });
      throw err;
    } finally {
      this.isRunning.set(false);
    }
  }

  // ============================================================
  // Per-phase helpers
  // ============================================================

  private async uploadChannels(uid: string, channels: SavedChannel[]): Promise<void> {
    const fs = this.firebaseService.firestore!;
    if (channels.length === 0) return;

    for (let i = 0; i < channels.length; i += FIRESTORE_BATCH_LIMIT) {
      const slice = channels.slice(i, i + FIRESTORE_BATCH_LIMIT);
      const batch = writeBatch(fs);
      for (const ch of slice) {
        batch.set(
          doc(fs, 'users', uid, 'channels', ch.id),
          {
            id: ch.id,
            name: ch.name,
            login: ch.login,
            profileImageUrl: ch.profileImageUrl,
            addedAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        );
      }
      await batch.commit();
    }
  }

  private async uploadProjectsMetadata(uid: string, projects: StreamProject[]): Promise<void> {
    const fs = this.firebaseService.firestore!;
    if (projects.length === 0) return;

    let written = 0;
    for (let i = 0; i < projects.length; i += PROJECTS_PER_BATCH) {
      const slice = projects.slice(i, i + PROJECTS_PER_BATCH);
      const batch = writeBatch(fs);
      for (const p of slice) {
        batch.set(
          doc(fs, 'users', uid, 'projects', p.videoId),
          {
            videoId: p.videoId,
            channelId: p.channelId,
            title: p.title,
            createdAt: p.createdAt,
            duration: p.duration,
            thumbnailUrl: p.thumbnailUrl,
            isChatDownloaded: !!p.isChatDownloaded,
            userColor: p.userColor ?? null,
            lastModifiedAt: p.lastModifiedAt ?? Date.now(),
            updatedAt: serverTimestamp(),
            deletedAt: null,
          },
          { merge: true },
        );
      }
      await batch.commit();
      written += slice.length;
      this.progress.set({
        phase: 'projects',
        current: written,
        total: projects.length,
        message: `Метаданные проектов: ${written}/${projects.length}`,
      });
    }
  }

  private async uploadProjectStates(uid: string, projects: StreamProject[]): Promise<void> {
    const fs = this.firebaseService.firestore!;

    let processed = 0;
    for (const p of projects) {
      try {
        const idbState = await this.idbService.get(`project_state_${p.videoId}`);
        if (idbState && typeof idbState === 'object') {
          const stripped = this.stripChatComments(idbState);
          await setDoc(
            doc(fs, 'users', uid, 'projects', p.videoId, 'state', 'main'),
            this.serializeStateForFirestore(stripped),
          );
        }
      } catch (err) {
        console.warn(`Migration: state for ${p.videoId} skipped:`, err);
      }
      processed++;
      this.progress.set({
        phase: 'state',
        current: processed,
        total: projects.length,
        message: `Состояния проектов: ${processed}/${projects.length}`,
      });
    }
  }

  private async uploadActiveSession(uid: string, knownProjects: StreamProject[]): Promise<void> {
    const fs = this.firebaseService.firestore!;
    let session: any;
    try {
      const raw = localStorage.getItem(STORAGE_KEYS.SESSION);
      if (!raw || raw === 'undefined' || raw === 'null') return;
      session = JSON.parse(raw);
    } catch {
      return;
    }
    if (!session || !session.videoId) return;

    const videoId = String(session.videoId);
    // If already covered by knownProjects, the per-project loop wrote a fresher
    // state from IDB (same key). Skip duplicate write.
    if (knownProjects.some((p) => p.videoId === videoId)) return;

    const stripped = this.stripChatComments(session);
    try {
      await setDoc(
        doc(fs, 'users', uid, 'projects', videoId, 'state', 'main'),
        this.serializeStateForFirestore(stripped),
      );
    } catch (err) {
      console.warn(`Migration: active session ${videoId} state skipped:`, err);
    }
  }

  private async uploadSettings(uid: string): Promise<void> {
    const fs = this.firebaseService.firestore!;
    const settings: GlobalSettings = {};

    const searchHistory = this.readJson<string[]>(STORAGE_KEYS.SEARCH_HISTORY);
    if (Array.isArray(searchHistory)) settings.searchHistory = searchHistory;

    // UI scale + overlay toggle live in the session blob; surface them as global settings
    try {
      const sessionRaw = localStorage.getItem(STORAGE_KEYS.SESSION);
      if (sessionRaw && sessionRaw !== 'undefined' && sessionRaw !== 'null') {
        const session = JSON.parse(sessionRaw);
        if (typeof session.interfaceScale === 'number') settings.interfaceScale = session.interfaceScale;
        if (typeof session.isChatOverlayEnabled === 'boolean')
          settings.isChatOverlayEnabled = session.isChatOverlayEnabled;
        if (session.heatmapScale === 'log' || session.heatmapScale === 'linear')
          settings.heatmapScale = session.heatmapScale;
        if (session.sortOrder === 'time' || session.sortOrder === 'count')
          settings.sortOrder = session.sortOrder;
      }
    } catch {
      /* ignore */
    }

    if (Object.keys(settings).length === 0) return;

    await setDoc(
      doc(fs, 'users', uid, 'settings', 'global'),
      { ...settings, updatedAt: serverTimestamp() },
      { merge: true },
    );
  }

  // ============================================================
  // Utilities
  // ============================================================

  private readJson<T>(key: string): T | null {
    try {
      const raw = localStorage.getItem(key);
      if (!raw || raw === 'undefined' || raw === 'null') return null;
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  }

  private stripChatComments(state: any): any {
    if (!state || typeof state !== 'object') return state;
    const { chatComments, ...rest } = state;
    return rest;
  }

  private serializeStateForFirestore(state: any): any {
    const out: any = { ...state };

    if (Array.isArray(state.analysisCache)) {
      out.analysisCache = Object.fromEntries(
        state.analysisCache.map(([k, v]: [number | string, unknown]) => [String(k), v]),
      );
    }
    if (Array.isArray(state.highlightStateCache)) {
      out.highlightStateCache = Object.fromEntries(
        state.highlightStateCache.map(([k, v]: [number | string, unknown]) => [String(k), v]),
      );
    }
    out.updatedAt = serverTimestamp();
    out.schemaVersion = SYNC_SCHEMA_VERSION;
    return removeUndefined(out);
  }
}

function removeUndefined<T>(obj: T): T {
  if (obj === null || obj === undefined) return obj;
  if (Array.isArray(obj)) return obj.map(removeUndefined) as any;
  if (typeof obj !== 'object') return obj;
  const out: any = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) out[k] = removeUndefined(v);
  }
  return out;
}
